import {
  AdminSelect,
  AdminSpinner,
  BestPerforming,
  Box,
  displayCentsInDollarsShort,
  Div,
  DragScrollX,
  formatAmount,
  getDashboardClientsOverHundredCountFinances,
  getDashboardClientsOverHundredFinances,
  getDashboardTotalBlueCardBalanceFinances,
  getDashboardTotalBlueCardWithdrawalFinances,
  getDashboardVPayBalanceFinances,
  H2,
  H3,
  H5,
  lightTheme,
  NumberType,
  TransactionStatus,
  VPayBalanceResponse,
  VPayLogoBlueCircle,
  reportError,
} from '@vgrubs/components';
import { isAxiosError } from 'axios';
import { useEffect, useState } from 'react';

const clientsOver = [
  10000, 20000, 30000, 40000, 50000, 100000, 500000, 1000000,
];

const clientsOverOptions = (
  <optgroup label="Select Amount">
    {clientsOver.map((r, i) => (
      <option value={r} key={i}>
        {displayCentsInDollarsShort(r)}
      </option>
    ))}
  </optgroup>
);

export const BalanceFinances = () => {
  const [loading, setLoading] = useState(false);

  const [balanceFinances, setBalanceFinances] =
    useState<VPayBalanceResponse | null>(null);
  const [clientsOverAmount, setClientsOverAmount] = useState(clientsOver[0]);
  const [clientsOverHundredCount, setClientsOverHundredCount] = useState(0);
  async function getDashboard() {
    try {
      setLoading(true);

      const { data: vPayBalance } = await getDashboardVPayBalanceFinances();
      setBalanceFinances(vPayBalance.data);
    } catch (error) {
      if (
        isAxiosError(error) &&
        error.response?.data.message !== 'Unauthorized'
      ) {
        reportError(error);
      }
    } finally {
      setLoading(false);
    }
  }

  async function getDashboardClientsOverHundredCount() {
    try {
      const { data: clientsOverHundredCount } =
        await getDashboardClientsOverHundredCountFinances({
          page: 1,
          limit: 1,
          amount: clientsOverAmount,
        });
      setClientsOverHundredCount(clientsOverHundredCount.data.count);
    } catch (error) {
      if (
        isAxiosError(error) &&
        error.response?.data.message !== 'Unauthorized'
      ) {
        reportError(error);
      }
    }
  }

  useEffect(() => {
    getDashboard();
  }, []);

  useEffect(() => {
    getDashboardClientsOverHundredCount();
  }, [clientsOverAmount]);

  return (
    <Div>
      <H3 $mb="16">Bank Info / Transactions</H3>
      {loading && <AdminSpinner />}
      <DragScrollX>
        <TotalBankBalance balanceFinances={balanceFinances} />
        <BestPerforming
          title="Total vPay Card Balance"
          getFunction={getDashboardTotalBlueCardBalanceFinances}
          valueColor={lightTheme.blue}
          totalAmount={formatAmount(
            balanceFinances?.totalBlueCardBalance || 0,
            NumberType.USD
          )}
          link="/users"
        />
        <BestPerforming
          title="Number of Clients with Balance Over"
          getFunction={({ page, limit }) =>
            getDashboardClientsOverHundredFinances({
              page,
              limit,
              amount: clientsOverAmount,
            })
          }
          valueColor={lightTheme.blue}
          totalAmount={clientsOverHundredCount.toString()}
          customComponent={
            <AdminSelect
              value={clientsOverAmount || ''}
              options={clientsOverOptions}
              onChange={({ target }) => {
                setClientsOverAmount(Number(target.value));
              }}
              $small
              $bold
              $color={lightTheme.blue}
            />
          }
          customDependencies={clientsOverAmount}
          link="/users"
        />
        <BestPerforming
          title="Total Completed Withdrawals"
          getFunction={({ page, limit }) =>
            getDashboardTotalBlueCardWithdrawalFinances({
              status: TransactionStatus.processed,
              page,
              limit,
            })
          }
          valueColor={lightTheme.blue}
          totalAmount={formatAmount(
            (balanceFinances?.completedBlueCardWithrawal || 0) + 152815675,
            NumberType.USD
          )}
          noLastActive
          link="/users"
        />
        <BestPerforming
          title="Total Pending Withdrawals"
          getFunction={({ page, limit }) =>
            getDashboardTotalBlueCardWithdrawalFinances({
              status: TransactionStatus.pending,
              page,
              limit,
            })
          }
          valueColor={lightTheme.orange}
          totalAmount={formatAmount(
            balanceFinances?.pendingBlueCardWithrawal || 0,
            NumberType.USD
          )}
          noLastActive
          link="/users"
        />
      </DragScrollX>
    </Div>
  );
};

const TotalBankBalance = ({
  balanceFinances,
}: {
  balanceFinances: VPayBalanceResponse | null;
}) => {
  return (
    <Box
      $width="300px !important"
      $minWidth="300px !important"
      $widthSM="80vw !important"
      $minWidthSM="80vw !important"
      $p="16px 20px"
      $relative
      $overflow="hidden"
      $flex
      $center
      $column
      $gap="16px"
    >
      <Div>
        <VPayLogoBlueCircle />

        <H3 $center $mt="16" $mb="0">
          Bank vPay
        </H3>
      </Div>
      <div>
        <H5 $center $m="0">
          Total Bank Balance
        </H5>
        <H2 $center $m="0" $color={lightTheme.blue}>
          {formatAmount(balanceFinances?.balance || 0, NumberType.USD)}
        </H2>
      </div>
    </Box>
  );
};
